/**
 * Player-name validation (SPEC §2.1): first name, 12 characters, profanity
 * filtered. The server validates the same rules; this is for instant feedback.
 */
import { MAX_NAME_LENGTH, MIN_NAME_LENGTH, PlayerNameSchema } from '@idgame/protocol';
import { isProfane } from './profanity.js';

export type NameProblem = 'empty' | 'too-long' | 'profane' | 'invalid';

export interface NameValidation {
  ok: boolean;
  value: string;
  problem: NameProblem | null;
  message: string | null;
}

/** Trims, then applies the protocol schema and the profanity list. */
export function validateName(raw: string): NameValidation {
  const value = raw.trim();

  if (value.length < MIN_NAME_LENGTH) {
    return { ok: false, value, problem: 'empty', message: 'Enter a first name.' };
  }
  if (value.length > MAX_NAME_LENGTH) {
    return {
      ok: false,
      value,
      problem: 'too-long',
      message: `${MAX_NAME_LENGTH} characters max.`,
    };
  }
  if (isProfane(value)) {
    return { ok: false, value, problem: 'profane', message: 'Pick something else.' };
  }
  if (!PlayerNameSchema.safeParse(value).success) {
    return { ok: false, value, problem: 'invalid', message: "That name won't work." };
  }

  return { ok: true, value, problem: null, message: null };
}

/** Hard cap applied while typing, so the field cannot exceed the wire limit. */
export function clampName(raw: string): string {
  return raw.replace(/\s+/g, ' ').trimStart().slice(0, MAX_NAME_LENGTH);
}
